<script lang="ts" setup>
import { computed } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { useFlowSteps } from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import useCollectSubmit from '@Use/useCollectSubmit';
import CollectAccountInfo from '@Components/CollectAccountInfo.vue';
import PlaidConnectModal from '@Components/modals/PlaidConnectModal.vue';
import PlaidPrimingStep from '@Components/shared/PlaidPrimingStep.vue';
import { AccountHolderType } from '@Enums/accountHolderType.enum';
import { FlowLayout } from '@Shared';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const { goBackInFlow } = useFlowNavigation();

const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { taskNavTitle } = useTaskNavTitle();

// Submit + optional Plaid upgrade (PLAT-2857) — action switch lives in the composable.
const {
    isSubmitting,
    errorMessage,
    plaidLinkToken,
    isPlaidModalOpen,
    isPrimingPlaid,
    isCompletingPlaid,
    submit,
    continueToPlaid,
    onPlaidSuccess,
    onPlaidExit,
} = useCollectSubmit();
/** See ConfirmReviewView: no walking back out of an already-finalized request mid-Plaid. */
const onBackClick = () => {
    if (isPrimingPlaid.value || isCompletingPlaid.value) return;
    goBackInFlow();
};


/**
 * PLAT-3373: whether account details made the date of birth a required field for this request.
 * Mirrors the condition there exactly, including treating an absent holder type as Individual —
 * a pre-PLAT-3157 record is a person until something says otherwise.
 */
const dateOfBirthRequired = computed(() =>
    companyStore.accountValidationCaptureEnabled
    && requestSessionStore.collectWiringInstructions.creditTo?.accountHolderType !== AccountHolderType.Business);

// Actions
const onSubmit = async () => {
    // The date of birth is deliberately not persisted, so a page reload drops it while the rest
    // of the wiring is restored from localStorage. Finalizing here would send null for a field
    // account details made required — the capture would be silently skipped, and nothing
    // downstream would know the difference. Send the participant back to re-enter it instead.
    if (dateOfBirthRequired.value && !requestSessionStore.collectAccountHolderDateOfBirth) {
        goBackInFlow();
        return;
    }

    const wi = requestSessionStore.collectWiringInstructions;
    await submit({
        bankName: wi.beneficiaryBank?.name ?? wi.bankName ?? '',
        accountNumber: wi.creditTo?.accountNumber ?? wi.accountNumber ?? '',
        routingNumber: wi.beneficiaryBank?.routingNumber ?? wi.routingNumber ?? '',
        accountType: wi.bankAccountInfo?.accountType ?? wi.accountType ?? '',
        accountHolderName: wi.creditTo?.accountName ?? wi.accountHolderName ?? '',
        isJoint: wi.bankAccountInfo?.isJoint ?? false,
        accountHolderType: wi.creditTo?.accountHolderType ?? null,
        accountHolderFirstName: wi.creditTo?.accountHolderFirstName ?? null,
        accountHolderLastName: wi.creditTo?.accountHolderLastName ?? null,
        accountHolderBusinessName: wi.creditTo?.accountHolderBusinessName ?? null,
        // PLAT-3373: already the ISO wire form by the time it reaches the store.
        // From the session store's non-persisted field, not the wiring (PLAT-3373: never localStorage).
        accountHolderDateOfBirth: requestSessionStore.collectAccountHolderDateOfBirth ?? null,
        personalAddress: {
            address1: wi.personalAddress?.address1 ?? '',
            address2: wi.personalAddress?.address2 ?? '',
            city: wi.personalAddress?.city ?? '',
            state: wi.personalAddress?.state ?? '',
            postalCode: wi.personalAddress?.postalCode ?? '',
        },
        furtherCreditToAccountName: wi.furtherCreditTo?.accountName ?? '',
        furtherCreditToAccountNumber: wi.furtherCreditTo?.accountNumber ?? '',
    });
};
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        :show-back-button="!isPrimingPlaid && !isCompletingPlaid"
        @back-click="onBackClick"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <!-- Full-page loader while the Plaid completion call records the attempt; without it
             the review screen re-appears for a few seconds after the Plaid overlay closes. -->
        <app-page-loader v-if="isCompletingPlaid" />

        <!-- PLAT-3131: the Plaid offer takes over the step. The request is already complete and
             certified by this point, so this is an offer rather than another thing to get past. -->
        <plaid-priming-step
            v-else-if="isPrimingPlaid"
            @continue="continueToPlaid"
        />

        <app-form-content-layout
            v-else
            class="collect-review__content"
        >
            <h1 class="collect-review__headline">Review account details</h1>

            <p class="collect-review__description">
                The funds will be deposited to the bank account below. Please double-check these details before submitting.
            </p>

            <p
                v-if="errorMessage"
                class="collect-review__error"
                role="alert"
            >
                {{ errorMessage }}
            </p>

            <collect-account-info
                :wiring-instructions="requestSessionStore.collectWiringInstructions"
                toggle-data-id="collect-review-account-details-toggle"
                collapse-initial-state="opened"
            />
        </app-form-content-layout>

        <div
            v-if="!isCompletingPlaid && !isPrimingPlaid"
            class="collect-review__buttons"
        >
            <app-button
                class="collect-review__button"
                expanded
                size="large"
                :is-loading="isSubmitting"
                data-id="collect-review-submit"
                @onClick="onSubmit"
            >
                Submit
            </app-button>
        </div>

        <plaid-connect-modal
            v-if="isPlaidModalOpen && plaidLinkToken"
            v-model="isPlaidModalOpen"
            :link-token="plaidLinkToken"
            @on-success="onPlaidSuccess"
            @on-exit="onPlaidExit"
        />
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.collect-review {
    &__content {
        margin-bottom: 2rem;
    }

    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        margin: 0;
    }

    &__buttons {
        display: flex;
        flex-direction: column;
    }
}
</style>
